"use client";

import { useEffect, useState } from 'react';
import axios from 'axios';
import Link from 'next/link';
import { useSearchParams } from 'next/navigation';
import { EventItem } from '@/types/event';
import { ApiResponse } from '@/types/api';
import { useTranslation } from '@/context/LanguageContext';
import SearchBar from '@/components/ui/SearchBar';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faChevronLeft, faChevronRight, faHome, faCalendarTimes, faArrowRight
} from '@fortawesome/free-solid-svg-icons';
import { faClock } from '@fortawesome/free-regular-svg-icons';

interface EventListClientProps {
    type: number;
    typeName: string;
}

import { useUnit } from '@/context/UnitContext';

// ...

export default function EventListClient({ type, typeName }: EventListClientProps) {
    const { unitId } = useUnit();
    const { language } = useTranslation();
    const searchParams = useSearchParams();
    const searchQuery = searchParams.get('search') || '';
    const [events, setEvents] = useState<EventItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [currentPage, setCurrentPage] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [isMounted, setIsMounted] = useState(false);
    const limit = 9;

    useEffect(() => {
        setIsMounted(true);
    }, []);

    useEffect(() => {
        // Reset to first page when search query changes
        setCurrentPage(0);
    }, [searchQuery]);

    useEffect(() => {
        if (!unitId) return;

        const fetchEvents = async () => {
            setLoading(true);
            try {
                const keywordParam = searchQuery ? `&keyword=${encodeURIComponent(searchQuery)}` : '';
                const response = await axios.post<ApiResponse<EventItem[]>>('/api/doServices', {
                    method: 'GET',
                    endpoint: `web/event?limit=${limit}&page=${currentPage + 1}&unit_id=${unitId}&order=CREATED_AT DESC&type=${type}${keywordParam}`
                });

                const apiData = response.data;
                if (apiData && apiData.data) {
                    setEvents(apiData.data);
                    setTotalPages(Number(apiData.metadata?.total_page) || 0);
                } else {
                    setEvents([]);
                    setTotalPages(0);
                }
            } catch (error: any) {
                setEvents([]);
            } finally {
                setLoading(false);
            }
        };
        fetchEvents();
    }, [currentPage, type, searchQuery]);

    const handlePageChange = (page: number) => {
        setCurrentPage(page);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const renderPagination = () => {
        if (totalPages <= 1) return null;

        const pages = [];
        const maxVisible = 5;
        let start = Math.max(0, currentPage - Math.floor(maxVisible / 2));
        let end = Math.min(totalPages, start + maxVisible);

        if (end - start < maxVisible) {
            start = Math.max(0, end - maxVisible);
        }

        if (currentPage > 0) {
            pages.push(
                <button
                    key="prev"
                    onClick={() => handlePageChange(currentPage - 1)}
                    className="w-10 h-10 bg-white text-gray-600 rounded-lg flex items-center justify-center hover:bg-gray-100"
                >
                    <FontAwesomeIcon icon={faChevronLeft} />
                </button>
            );
        }

        for (let i = start; i < end; i++) {
            pages.push(
                <button
                    key={i}
                    onClick={() => handlePageChange(i)}
                    className={`w-10 h-10 rounded-lg flex items-center justify-center font-bold ${i === currentPage
                        ? 'bg-unmulGreen text-white'
                        : 'bg-white text-gray-600 hover:bg-gray-100'
                        }`}
                >
                    {i + 1}
                </button>
            );
        }

        if (end < totalPages) {
            pages.push(
                <span key="ellipsis" className="w-10 h-10 flex items-center justify-center text-gray-400">
                    ...
                </span>
            );
            pages.push(
                <button
                    key={totalPages - 1}
                    onClick={() => handlePageChange(totalPages - 1)}
                    className="w-10 h-10 bg-white text-gray-600 rounded-lg flex items-center justify-center hover:bg-gray-100"
                >
                    {totalPages}
                </button>
            );
        }

        if (currentPage < totalPages - 1) {
            pages.push(
                <button
                    key="next"
                    onClick={() => handlePageChange(currentPage + 1)}
                    className="w-10 h-10 bg-white text-gray-600 rounded-lg flex items-center justify-center hover:bg-gray-100"
                >
                    <FontAwesomeIcon icon={faChevronRight} />
                </button>
            );
        }

        return pages;
    };

    const getImageUrl = (event: EventItem) => {
        if (event.filename) {
            return `https://unmul.ac.id/uploads/events/${event.filename}`;
        }
        return `/api/doServicesProxy?endpoint=web/file/event/${event.id}&isImage=true`;
    };

    const formatDate = (dateString: string) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date.toLocaleDateString(language === 'en' ? 'en-US' : 'id-ID', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    return (
        <>
            {/* Hero Header */}
            <section className="pt-36 pb-16 bg-unmulGreen relative overflow-hidden">
                <div className="absolute inset-0 opacity-10">
                    <svg className="absolute w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none">
                        <path d="M0 100 C 20 0 50 0 100 100 Z" fill="white" />
                    </svg>
                    <div className="absolute top-10 right-10 w-48 h-48 bg-unmulYellow rounded-full blur-3xl opacity-30"></div>
                    <div className="absolute bottom-10 left-10 w-64 h-64 bg-white rounded-full blur-3xl opacity-20"></div>
                </div>

                <div className="container mx-auto px-6 relative z-10">
                    <nav className="flex items-center gap-2 text-sm text-white/80 mb-6">
                        <Link href="/" className="hover:text-white transition"><FontAwesomeIcon icon={faHome} /></Link>
                        <FontAwesomeIcon icon={faChevronRight} className="text-xs text-white/50" />
                        <span className="text-white">Event</span>
                        <FontAwesomeIcon icon={faChevronRight} className="text-xs text-white/50" />
                        <span className="text-unmulYellow font-medium">{typeName}</span>
                    </nav>

                    <h1 className="text-4xl md:text-5xl font-serif font-bold text-white mb-4">
                        {typeName}
                    </h1>
                    <p className="text-white/80 text-lg max-w-2xl">
                        {language === 'en'
                            ? 'Browse our upcoming events and activities'
                            : 'Jelajahi acara dan kegiatan kami yang akan datang'
                        }
                    </p>
                </div>
            </section>

            {/* Events Grid */}
            <section className="py-12 bg-unmulPale min-h-screen">
                <div className="container mx-auto px-6">
                    <SearchBar placeholder={language === 'en' ? "Search events..." : "Cari acara..."} className="mb-10" />

                    {loading ? (
                        <div className="flex justify-center items-center py-20">
                            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-unmulGreen"></div>
                        </div>
                    ) : events.length === 0 ? (
                        <div className="text-center py-20">
                            <div className="w-24 h-24 mx-auto mb-6 bg-gray-100 rounded-full flex items-center justify-center">
                                <FontAwesomeIcon icon={faCalendarTimes} className="text-4xl text-gray-400" />
                            </div>
                            <p className="text-gray-500 text-lg">
                                {searchQuery
                                    ? (language === 'en' ? `No events found for "${searchQuery}"` : `Tidak ada acara untuk "${searchQuery}"`)
                                    : (language === 'en' ? 'No events available.' : 'Belum ada event yang tersedia.')
                                }
                            </p>
                        </div>
                    ) : (
                        <>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                {events.map((event, index) => (
                                    <Link
                                        key={event.id}
                                        href={`/event/${event.slug}`}
                                        className="bg-white rounded-xl shadow-sm hover:shadow-2xl transition-all duration-300 overflow-hidden group"
                                        data-aos={isMounted ? "fade-up" : undefined}
                                        data-aos-delay={isMounted ? index * 50 : undefined}
                                    >
                                        <div className="relative h-48 overflow-hidden bg-gray-100">
                                            <img
                                                src={getImageUrl(event)}
                                                alt={language === 'en' ? event.title_en : event.title_idn}
                                                className="w-full h-full object-cover group-hover:scale-110 transition duration-500"
                                                loading="lazy"
                                                onError={(e) => {
                                                    const target = e.target as HTMLImageElement;
                                                    target.src = 'https://via.placeholder.com/400x300/047857/ffffff?text=Event';
                                                }}
                                            />
                                            <div className="absolute top-4 right-4 bg-unmulYellow text-unmulGreen px-3 py-1 rounded-full text-xs font-bold">
                                                {formatDate(event.start_time)}
                                            </div>
                                        </div>

                                        <div className="p-5">
                                            <h3 className="font-serif font-bold text-lg text-gray-900 group-hover:text-unmulGreen transition mb-3 line-clamp-2 leading-tight">
                                                {language === 'en' ? (event.title_en || event.title_idn) : (event.title_idn || event.title_en)}
                                            </h3>

                                            <div className="flex items-center gap-4 text-sm text-gray-500 mb-4">
                                                <span className="flex items-center gap-1">
                                                    <FontAwesomeIcon icon={faClock} />
                                                    {formatDate(event.start_time)}
                                                </span>
                                            </div>

                                            <div className="flex items-center justify-between">
                                                <span className="text-unmulGreen font-semibold text-sm group-hover:underline">
                                                    {language === 'en' ? 'Read More' : 'Baca Selengkapnya'}
                                                    <FontAwesomeIcon icon={faArrowRight} className="ml-2 text-xs" />
                                                </span>
                                            </div>
                                        </div>
                                    </Link>
                                ))}
                            </div>

                            {/* Pagination */}
                            <div className="mt-12 flex justify-center items-center gap-2">
                                {renderPagination()}
                            </div>
                        </>
                    )}
                </div>
            </section>
        </>
    );
}
